import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { isValid } from 'date-fns';
import { NGXLogger } from 'ngx-logger';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { SnackBarService } from '../../../shared/snackbar.service';
import { BillingSummary } from '../../../types';
import { openSANDiBillingList } from '../../../utilities/openSandiBilling';
import { ExportService } from '../../export.service';
import { LoadsForAllProductsService } from '../../loads-for-all-products.service';

@Component({
  selector: 'lohi-billing',
  templateUrl: './billing.component.html',
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BillingComponent implements OnInit {
  public exporting$$ = new BehaviorSubject<boolean>(false);
  public bulkExportFormGroup = new FormGroup({
    start: new FormControl(null, [Validators.required]),
    end: new FormControl(null, [Validators.required]),
  });
  public taxExportFormGroup = new FormGroup({
    start: new FormControl(null, [Validators.required]),
    end: new FormControl(null, [Validators.required]),
  });
  public loadsMissingPODCount$: Observable<number>;
  public loadsMissingPODCountMoreThan24Hours$: Observable<number>;
  public loadsAverageDaysToApprove$: Observable<number>;

  constructor(
    private exportService: ExportService,
    private logger: NGXLogger,
    private snackBar: SnackBarService,
    private loadsService: LoadsForAllProductsService,
    private cd: ChangeDetectorRef,
    private httpClient: HttpClient,
    private router: Router,
  ) {}

  public ngOnInit(): void {
    this.loadsMissingPODCount$ = this.loadsService.billingSummary$.pipe(map(countMissingPOD));
    this.loadsMissingPODCountMoreThan24Hours$ = this.loadsService.billingSummary$.pipe(
      map(countMissingPODMoreThan24Hours),
    );
    this.loadsAverageDaysToApprove$ = this.loadsService.billingSummary$.pipe(map(loadsAverageDaysToApprove));
  }

  public async exportBulkData() {
    if (this.exporting$$.value) {
      return;
    }
    if (this.bulkExportFormGroup.invalid) {
      this.bulkExportFormGroup.markAllAsTouched();
      return;
    }
    const value: { start: Date; end: Date } = this.bulkExportFormGroup.value;
    if (!isValid(value.start) || !isValid(value.end)) {
      return;
    }
    this.exporting$$.next(true);
    try {
      const urls = await this.exportService.getBulkExportUrl$(value.start, value.end).toPromise();
      for (const url of urls) {
        window.open(url);
      }
      this.resetBulkExportForm();
      this.bulkExportFormGroup.markAsUntouched();
    } catch (error) {
      this.logger.error(error);
      this.snackBar.showError('Failed to retrieve export');
    }
    this.exporting$$.next(false);
  }

  public resetBulkExportForm() {
    this.bulkExportFormGroup = new FormGroup({
      start: new FormControl(null, [Validators.required]),
      end: new FormControl(null, [Validators.required]),
    });
    this.cd.markForCheck();
  }

  public async exportTaxData() {
    if (this.exporting$$.value) {
      return;
    }
    if (this.taxExportFormGroup.invalid) {
      this.taxExportFormGroup.markAllAsTouched();
      return;
    }
    const value: { start: Date; end: Date } = this.taxExportFormGroup.value;
    if (!isValid(value.start) || !isValid(value.end)) {
      return;
    }
    this.exporting$$.next(true);
    try {
      const url = await this.exportService.getCompanyTaxExportUrl$(value.start, value.end).toPromise();
      window.open(url);

      this.resetTaxExportForm();
      this.taxExportFormGroup.markAsUntouched();
    } catch (error) {
      this.logger.error(error);
      this.snackBar.showError('Failed to retrieve export');
    }
    this.exporting$$.next(false);
  }

  public resetTaxExportForm() {
    this.taxExportFormGroup = new FormGroup({
      start: new FormControl(null, [Validators.required]),
      end: new FormControl(null, [Validators.required]),
    });
    this.cd.markForCheck();
  }

  public goToSandiBilling() {
    openSANDiBillingList(this.httpClient, this.logger);
  }

  public navigateToLoadsList(filterValue: string) {
    this.router.navigate(['loads/list/completed'], {
      queryParams: {
        [filterValue]: true,
      },
    });
  }
}

function countMissingPOD(summary: BillingSummary): number {
  return summary.data.missingPODCount ?? 0;
}

function countMissingPODMoreThan24Hours(summary: BillingSummary): number {
  return summary.data.missingPODMoreThan24HrsCount ?? 0;
}

function loadsAverageDaysToApprove(summary: BillingSummary): number {
  return summary.data.averageWorkingDaysToApprove ?? 0;
}
